import { FlexSection, HighlightedSection } from "components/containers";
import { FC, FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ResponsiveImage } from "../../../components/ResponsiveImage";
import {
  Title,
  ConceptCard,
  ReviewSection,
  ContactForm,
  ContactSection,
  DataGrid,
  Content
} from "./styles";
import { Mail, Phone } from "../../../components/Icons/Generic";
import { BlogCard } from "../../../components/BlogCard";
import { InputLabel } from "../../../components/Input";
import { Button } from "../../../components/Button";
import { useNotifier } from "../../../context/ContextNotifier";
import { OrangePlayFull } from "components/Icons/OrangePlayFull";
import { useIsMobile } from "../../../utils/hooks/useWindowSize";
import { HeaderSEO } from "components/headerSEO";
import { ISeo } from "interfaces/interfaces";
import { Review } from "../../../landing-components/Review";
import reviewer from "assets/images/GreteVisser.webp";

const Partners: FC = () => {
  const { t, i18n } = useTranslation("partners");
  const [loading, setLoading] = useState(false);
  const notifier = useNotifier();
  const isMobile = useIsMobile();

  const sendData = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "partner",
        first_name: e.currentTarget.first_name.value,
        phone: e.currentTarget.phone.value,
        browser_language: i18n.language
      })
    };

    fetch(
      process.env.REACT_APP_AWS_ENDPOINT! +
        `public/contact-us/${e.currentTarget.email.value as string}`,
      requestOptions
    )
      .then((response) => response.json())
      .then(() => {
        notifier.success({
          message: "Request sent"
        });
      })
      .finally(() => {
        setLoading(false);
      });
    e.currentTarget.reset();
  };

  const [textWidth, setTextWidth] = useState<string>("275px");

  useEffect(() => {
    if (isMobile) {
      setTextWidth("175px");
    } else {
      setTextWidth("275px");
    }
  }, [isMobile]);
  const seo = t("seo", { returnObjects: true }) as ISeo;

  return (
    <Content>
      {seo && <HeaderSEO {...seo} />}
      <Title>{t("partners.title")}</Title>
      <OrangePlayFull
        viewView={textWidth}
        viewHeight="12px"
        marginBottom="0.3em"
        strokeWidth="8"
      />
      <FlexSection grow={40}>
        <ResponsiveImage
          imgSrc={
            "rootpage/partners/Joost Bokkers geeft presentatie over kamertekort en Hospi Housing"
          }
          mobileSize={"95vw"}
          tabletSize={"40vw"}
          desktopSize={"50vw"}
          aspectRatio={"4/3"}
          minWidth={"22rem"}
        />
        <div>
          <h3>{t("partners.philosophy.header")}</h3>
          <h2>{t("partners.philosophy.title")}</h2>
          <p>{t("partners.philosophy.description")}</p>
        </div>
      </FlexSection>
      <section>
        <h3>{t("partners.concept.header")}</h3>
        <h2>{t("partners.concept.title")}</h2>
        <FlexSection grow={20}>
          <ConceptCard>
            <div>1</div>
            {t("partners.concept.1.title")}
            <p>{t("partners.concept.1.description")}</p>
          </ConceptCard>
          <ConceptCard>
            <div>2</div>
            {t("partners.concept.2.title")}
            <p>{t("partners.concept.2.description")}</p>
          </ConceptCard>
          <ConceptCard>
            <div>3</div>
            {t("partners.concept.3.title")}
            <p>{t("partners.concept.3.description")}</p>
          </ConceptCard>
        </FlexSection>
      </section>
      <FlexSection grow={40} reverse>
        <ResponsiveImage
          imgSrc={"team/joost"}
          mobileSize={"95vw"}
          tabletSize={"40vw"}
          desktopSize={"50vw"}
          aspectRatio={"4/3"}
          minWidth={"22rem"}
          loadingType="lazy"
        />
        <div>
          <h3>{t("partners.collaboration.header")}</h3>
          <h2>{t("partners.collaboration.title")}</h2>
          <p>{t("partners.collaboration.description")}</p>
          <HighlightedSection>{Mail}joost@hospihousing.com</HighlightedSection>
          <HighlightedSection>{Phone}06 13388424</HighlightedSection>
        </div>
      </FlexSection>
      <FlexSection grow={40}>
        <DataGrid>
          <div>
            <h2>50.000+</h2>
            <p>{t("partners.data.1")}</p>
          </div>
          <div>
            <h2>185+</h2>
            <p>{t("partners.data.2")}</p>
          </div>
          <div>
            <h2>3500+</h2>
            <p>{t("partners.data.3")}</p>
          </div>
          <div>
            <h2>4,7 / 5</h2>
            <p>{t("partners.data.4")}</p>
          </div>
        </DataGrid>
        <div>
          <h3>{t("partners.data.header")}</h3>
          <h2>{t("partners.data.title")}</h2>
          <p>{t("partners.data.description")}</p>
        </div>
      </FlexSection>
      <ReviewSection>
        <Review
          reviewText={t("partners.quote.text")}
          authorName={t("partners.quote.authorName")}
          authorInfo={t("partners.quote.authorInfo")}
          src={reviewer}
        />
      </ReviewSection>
      <section>
        <h3>{t("partners.proud.header")}</h3>
        <h2>{t("partners.proud.title")}</h2>
        <FlexSection grow={30} notCentering>
          <BlogCard
            post_id={"gemeente-maastricht-start-samenwerking-hospi-housing"}
            short_desc={true}
          />
          <BlogCard
            post_id={"gemeente-groningen-start-samenwerking-hospi-housing"}
            short_desc={true}
          />
          <BlogCard
            post_id={"kamer-verhuren-in-een-sociale-huurwoning"}
            short_desc={true}
          />
        </FlexSection>
      </section>
      <ContactSection>
        <FlexSection grow={20}>
          <div style={{ minWidth: "20em" }}>
            <h2>{t("partners.call.title")}</h2>
            <p>{t("partners.call.description")}</p>
          </div>
          <ContactForm
            onSubmit={(e) => sendData(e as FormEvent<HTMLFormElement>)}
          >
            <h2>Contact</h2>
            <p>{t("partners.call.name")}</p>
            <InputLabel
              name="first_name"
              placeholder={t("partners.call.name")}
              required
            />
            <p>{t("partners.call.email")}</p>
            <InputLabel
              name="email"
              placeholder={t("partners.call.email")}
              pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
              required
            />
            <p>{t("partners.call.phone")}</p>
            <InputLabel
              name="phone"
              placeholder={t("partners.call.phone")}
              pattern="^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$"
            />
            <Button type="submit" loading={loading}>
              {t("partners.call.contact")}
            </Button>
          </ContactForm>
        </FlexSection>
      </ContactSection>
    </Content>
  );
};

export default Partners;
